import { useCallback, useEffect, useRef } from 'react';
import { useRecoilState } from 'recoil';
import {
    patientConflictState,
    type PatientConflictState,
} from '~/hooks/patient/patientConflictAtom';
import type { PatientConflict } from '~/hooks/patient/usePatientGuard';

export type { PatientConflictState };

/**
 * The user's answer to a patient conflict. `loadBoth` is only ever offered
 * outside clinical mode.
 */
export type PatientConflictAction = 'discard' | 'switch' | 'loadBoth';

/** The open question's resolver, and the hook instance that asked it. */
let pending: {
    asker: object;
    resolve: (action: PatientConflictAction) => void;
} | null = null;

function settlePending(action: PatientConflictAction): void {
    const settled = pending;
    pending = null;
    settled?.resolve(action);
}

/**
 * Asks the user how to resolve a patient conflict and awaits the answer.
 *
 * The question is shared by the whole app: any instance can ask, and the one
 * dialog the app mounts answers through any instance. A question that can no
 * longer be answered resolves as `discard`, so nothing is committed without
 * an answer: one replaced by a newer question, one still open when the
 * instance that asked it unmounts, and one asked after that.
 */
export default function usePatientConflictPrompt() {
    const [patientConflict, setPatientConflict] =
        useRecoilState(patientConflictState);
    const askerRef = useRef({});
    const unmountedRef = useRef(false);

    const resolvePatientConflict = useCallback(
        (action: PatientConflictAction): void => {
            setPatientConflict((previous) => ({ ...previous, open: false }));
            settlePending(action);
        },
        [setPatientConflict]
    );

    const askPatientConflict = useCallback(
        (conflict: PatientConflict): Promise<PatientConflictAction> => {
            if (unmountedRef.current) {
                return Promise.resolve('discard');
            }
            settlePending('discard');
            return new Promise<PatientConflictAction>((resolve) => {
                pending = { asker: askerRef.current, resolve };
                setPatientConflict({
                    open: true,
                    loadedPatientIds: conflict.loadedPatientIds,
                    incomingPatientIds: conflict.incomingPatientIds,
                    hasUnsavedChanges: conflict.hasUnsavedChanges,
                    clinicalUse: conflict.clinicalUse,
                });
            });
        },
        [setPatientConflict]
    );

    useEffect(() => {
        const asker = askerRef.current;
        unmountedRef.current = false;
        return () => {
            unmountedRef.current = true;
            if (pending?.asker === asker) {
                setPatientConflict((previous) => ({
                    ...previous,
                    open: false,
                }));
                settlePending('discard');
            }
        };
    }, [setPatientConflict]);

    return { patientConflict, resolvePatientConflict, askPatientConflict };
}
